import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Unstable_Grid2 as Grid } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { UseDateRange } from 'types/app';
import { useProtectedClient } from 'hooks';
import { DatePickers } from 'components';
import { getAppImage } from 'helpers';
import { APP_IMAGES } from 'app-constants';
import { IMGIX_HOST } from 'settings';
import theme from 'theme';

import { COLORS, global } from '../styles';
import { scrollToSection } from '../helpers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface Props {
  range: UseDateRange;
}

const HERO_IMAGE = `${IMGIX_HOST}/static/webapp/${APP_IMAGES.AdvocacyHero}?auto=format&height=1000`;

const styles = {
  heroBackground: {
    background: `url(${HERO_IMAGE}) center center/cover no-repeat`,
    minHeight: '80vh',
  },
  heroPaper: {
    marginY: theme.spacing(4),
    padding: theme.spacing(5),
    border: `1px solid ${COLORS.gray1}`,
    backgroundColor: COLORS.black1,
    borderRadius: theme.spacing(2),
    textAlign: 'center',
  },
  clientTitle: {
    ...global.header2,
    color: 'white',
    fontWeight: 300,
  },
  title: {
    ...global.header1,
    fontWeight: 800,
    color: 'white',
  },
  datePicker: {
    ...global.header3,
    color: 'white',
    padding: 0,
    backgroundColor: 'transparent',
    '&.Mui-focused': {
      '&:after': {
        border: 0,
      },
    },
    '& .MuiInput-input': {
      paddingRight: '8px !important',
    },
  },
  icon: {
    color: 'white',
    fontSize: 32,
  },
  cursor: {
    cursor: 'pointer',
  },
  action: {
    ...global.header4,
    color: 'white',
  },
} as const;

const AdvocacyOpening: FC<Props> = ({ range }) => {
  const { t } = useTranslation();
  const client = useProtectedClient();
  const { dateRange, startDate, endDate, setStartDate, setEndDate, setDateRange } = range;

  const selectProps = {
    sx: styles.datePicker,
    IconComponent: () => <ExpandMoreIcon sx={styles.icon} />,
  };

  return (
    <Grid
      container
      sx={styles.heroBackground}
      direction="column"
      justifyContent="center"
      alignItems="center"
    >
      <img alt="EveryoneSocial Logo" {...getAppImage(APP_IMAGES.AdvocacyLogo, 26)} />
      <Grid
        container
        xs={8}
        sx={styles.heroPaper}
        alignItems="center"
        justifyContent="center"
        direction="column"
        spacing={3}
      >
        <Grid>
          <Typography align="center" sx={styles.clientTitle}>
            {client.title}
          </Typography>
        </Grid>
        <Grid>
          <Typography align="center" sx={styles.title}>
            {t('advocacy:title')}
          </Typography>
        </Grid>
        <Grid>
          <DatePickers.Range
            dateRange={dateRange}
            startDate={startDate}
            endDate={endDate}
            onSetStart={setStartDate}
            onSetEnd={setEndDate}
            onSetRange={setDateRange}
            selectProps={selectProps}
          />
        </Grid>
      </Grid>
      <Grid
        container
        direction="column"
        alignItems="center"
        onClick={() => scrollToSection('introduction')}
        sx={styles.cursor}
      >
        <Grid>
          <Typography sx={styles.action}>{t('advocacy:letsGo')}</Typography>
        </Grid>
        <Grid>
          <ArrowDownwardIcon sx={styles.icon} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AdvocacyOpening;
